import type { TurnFailureDto } from '@dbrain/shared';
import { replyFailureEn } from './texts/reply-failure.en.js';
import { replyFailureTr } from './texts/reply-failure.tr.js';

/**
 * 16b.1: the card of a turn that ended without an answer (`no_visible_reply`), in the same words on the web and the
 * phone. Its details come from `turn.completed` (`failure`: why, each try's time, where the output limit cut it); a
 * turn from before v0.16b.1 has none and reads as the generic empty answer. The long-output card
 * (HataUzunDosya, HataUzunDosyaAyrinti) says what was cut and what to do and offers "Basit sürümle başla"; the empty
 * one (HataBosCevap) offers "Yeniden dene" only. Both fold the technical details: what happened, the tries, the time
 * and model, the run number.
 */
export interface ReplyFailureView {
  kind: 'long' | 'empty';
  title: string;
  /** the paragraphs under the title */
  body: string[];
  /** the last paragraph, its label in bold ("Ne yapabilirsiniz:") */
  advice: { lead: string | null; text: string };
  /** "Basit sürümle başla" */
  simpleStart: boolean;
  /** Ne oldu, Denemeler, Süre ve model (the run number is the caller's last row, with its copy button) */
  details: { key: 'what' | 'tries' | 'timeModel'; label: string; value: string }[];
  /** the chip's reason after its time ("dosya yarıda kaldı"); none for an empty answer */
  chipReason: string | null;
}

type Texts = typeof replyFailureTr | typeof replyFailureEn;

export function replyFailureTexts(locale: string): Texts {
  return locale === 'en' ? replyFailureEn : replyFailureTr;
}

const fill = (text: string, values: Record<string, string | number>) => text.replace(/\{\{(\w+)\}\}/g, (_m, key: string) => String(values[key] ?? ''));

const FILE_TOOLS = new Set(['write_file', 'append_file', 'edit']);

/** "8.192" in Turkish, "8,192" in English */
const number = (value: number, locale: string) => new Intl.NumberFormat(locale === 'en' ? 'en' : 'tr-TR').format(value);

function word(table: Readonly<Record<number, string>>, count: number): string {
  return table[count] ?? String(count);
}

/** A turn ended because the output limit cut its replies, in a tool call that was writing a file. */
function fileCut(failure: TurnFailureDto | null): boolean {
  return failure?.cause === 'output_limit' && failure.cut === 'tool_call' && FILE_TOOLS.has(failure.tool ?? '');
}

export function replyFailureView(input: {
  failure: TurnFailureDto | null;
  locale: string;
  profile: 'chat' | 'code';
  /** the turn's time as its chip says it ("4 dk 38 sn"); none under a second (16c.1: never "0 sn") */
  time: string | null;
  model: string | null;
  /** 16c.1: the model's call ended with an empty reply (`empty_reply`): the generic card, and it says so */
  empty?: boolean;
}): ReplyFailureView {
  const w = replyFailureTexts(input.locale);
  const { failure } = input;
  const tries = failure?.attempts.length ?? 0;
  const timeModel = [input.time, input.model].filter(Boolean).join(' · ');
  const details: ReplyFailureView['details'] = [];
  if (failure?.cause === 'output_limit') {
    const limit = number(failure.maxTokens ?? 0, input.locale);
    const file = fileCut(failure);
    details.push({ key: 'what', label: w.labels.what, value: fill(failure.cut === 'thinking' ? w.what.thinking : file ? w.what.file : w.what.call, { limit }) });
    const cut = failure.attempts.filter((a) => a.cut !== null).length;
    details.push({
      key: 'tries',
      label: w.labels.tries,
      value: tries === 1 ? w.tries.oneCut : cut === tries ? fill(w.tries.allCut, { count: tries, all: word(w.all, tries) }) : fill(w.tries.someCut, { count: tries, cut }),
    });
    if (timeModel) details.push({ key: 'timeModel', label: w.labels.timeModel, value: timeModel });
    const body = [
      [
        file && failure.path
          ? input.time
            ? fill(w.long.file, { file: failure.path, time: input.time })
            : fill(w.long.fileNoTime, { file: failure.path })
          : input.time
            ? fill(w.long.work, { time: input.time })
            : w.long.workNoTime,
        input.profile === 'code' ? (failure.changed ? w.long.changed : w.long.unchanged) : '',
      ]
        .filter(Boolean)
        .join(' '),
    ];
    return { kind: 'long', title: w.long.title, body, advice: { lead: w.long.adviceLead, text: w.long.advice }, simpleStart: true, details, chipReason: file ? w.chip.file : w.chip.limit };
  }
  details.push({ key: 'what', label: w.labels.what, value: failure || input.empty ? w.what.empty : w.what.unknown });
  if (failure) details.push({ key: 'tries', label: w.labels.tries, value: tries === 1 ? w.tries.oneEmpty : fill(w.tries.allEmpty, { count: tries, all: word(w.all, tries) }) });
  if (timeModel) details.push({ key: 'timeModel', label: w.labels.timeModel, value: timeModel });
  return {
    kind: 'empty',
    title: w.empty.title,
    body: [tries > 1 ? fill(w.empty.tries, { count: word(w.count, tries) }) : w.empty.once],
    advice: { lead: null, text: w.empty.advice },
    simpleStart: false,
    details,
    chipReason: null,
  };
}

/**
 * 16c.1: the codes that end a turn with the "no answer" card: the runtime's `no_visible_reply`, and a model call that
 * kept coming back empty (`empty_reply`, which showed the old line before)
 */
export const NO_ANSWER_CODES: readonly string[] = ['no_visible_reply', 'empty_reply'];

/** A time worth saying on the card: none under a second (it would read "0 sn") */
export function shownDuration(ms: number | null): number | null {
  return ms !== null && ms >= 1000 ? ms : null;
}

/** The chip's open list of a failed turn: each try with its time (no step finished, or none that explains the time). */
export function failureAttempts(failure: TurnFailureDto | null, locale: string): { label: string; ms: number }[] {
  if (!failure) return [];
  const w = replyFailureTexts(locale);
  return failure.attempts.map((a, i) => ({ label: fill(a.cut ? w.attempt[a.cut] : w.attempt.empty, { n: i + 1 }), ms: a.ms }));
}

/** The run number as the card shows it (the copy button copies all of it). */
export function shortRunId(runId: string): string {
  return runId.slice(0, 8);
}
